import { Auth0Client, GetTokenSilentlyOptions } from '@auth0/auth0-spa-js';

import config from './config';

export class AuthClient {
  private auth0Client: Auth0Client;

  constructor() {
    this.auth0Client = new Auth0Client({
      domain: config.domain,
      clientId: config.clientId,
      authorizationParams: {
        audience: config.audience,
        scope: config.scope,
        redirect_uri: window.location.origin,
      },
      useRefreshTokens: true,
      useRefreshTokensFallback: true,
      cacheLocation: 'memory',
    });
  }

  async authorize(): Promise<void> {
    await this.auth0Client.loginWithRedirect({
      authorizationParams: {
        connection: config.realm,
        prompt: 'login',
      },
    });
  }

  async signOut(): Promise<void> {
    await this.auth0Client.logout({
      logoutParams: {
        returnTo: `${window.location.origin}/login`,
      },
    });
  }

  async getTokenSilently(options?: GetTokenSilentlyOptions): Promise<string> {
    return await this.auth0Client.getTokenSilently(options);
  }

  async getIdTokenClaims(): Promise<string | undefined> {
    const claims = await this.auth0Client.getIdTokenClaims();
    return claims?.__raw;
  }

  async handleRedirectCallback(): Promise<void> {
    await this.auth0Client.handleRedirectCallback();
  }

  async isAuthenticated(): Promise<boolean> {
    return await this.auth0Client.isAuthenticated();
  }

  // Passing selectedTenantId lets the post login action enrich straight into that tenant
  // instead of redirecting to the tenant selection page. The action still validates the id
  // against the tenants the user is actually associated with, so this is a hint, not a grant.
  // Omitting it keeps the original behavior: Auth0 decides and may redirect to selection.
  async switchTenant(selectedTenantId?: string): Promise<void> {
    await this.auth0Client.loginWithRedirect(
      selectedTenantId ? { authorizationParams: { selectedTenantId } } : undefined,
    );
  }

  async authorizeMfa(): Promise<void> {
    await this.auth0Client.loginWithRedirect({
      authorizationParams: {
        audience: config.mfaUrl,
        scope: config.mfaScope,
        redirect_uri: `${window.location.origin}/profile/account?${this.getMfaActionQueryParam().combined}`,
      },
    });
  }

  getMfaActionQueryParam(): { key: string; value: string; combined: string } {
    const key = 'action';
    const value = 'mfa';
    const combined = `${key}=${value}`;
    return { key, value, combined };
  }
}

const client = new AuthClient();

export default client;
